import { IAlbum, IMerchandiseMetadata } from '@/data/models/album';
import {
  ICurrentChangeField,
  ICurrentChanges,
  ICurrentProductMetadataChangesGroup,
} from '@/types/product-current-changes-types';
import { getMerchDefaultDisplayName } from '@/components/common/ProductUtilities';
import { productCurrentChanges } from '@/utils/changeRequest/productCurrentChanges';
import _ from 'lodash';

type CurrentChangesContainer = {
  changesCounter: number;
};

const merchandiseMetadataFieldKeys: Array<keyof IMerchandiseMetadata> = [
  'color',
  'size',
  'additionalInformation',
  'productDescription',
  'merchandiseType',
];

// Normalizes values before comparison so whitespace and object key order do not create false changes.
const normalizeChangeValue = (value: unknown): unknown => {
  if (_.isNil(value)) {
    return null;
  }

  if (_.isString(value)) {
    return value.trim();
  }

  if (_.isDate(value)) {
    return value.toISOString();
  }

  if (_.isArray(value)) {
    return value.map(normalizeChangeValue);
  }

  if (_.isPlainObject(value)) {
    return _(value as Record<string, unknown>)
      .mapValues(normalizeChangeValue)
      .toPairs()
      .sortBy(([key]) => key)
      .fromPairs()
      .value();
  }

  return value;
};

// Stores one merchandise metadata field and increments the product metadata counter when it changed.
const addMerchandiseMetadataChange = (
  productMetadataChanges: ICurrentProductMetadataChangesGroup,
  key: string,
  originalValue: unknown,
  changeRequestValue: unknown
) => {
  const hasChanges = !_.isEqual(
    normalizeChangeValue(originalValue),
    normalizeChangeValue(changeRequestValue)
  );

  (
    productMetadataChanges as unknown as Record<
      string,
      ICurrentChangeField | number
    >
  )[key] = {
    hasChanges,
    value: _.cloneDeep(originalValue),
  };

  if (hasChanges) {
    productMetadataChanges.changesCounter += 1;
  }
};

// Adds merchandiseMetadata fields to the same productMetadataChanges object used by physical products.
const addMerchandiseMetadataChanges = (
  productMetadataChanges: ICurrentProductMetadataChangesGroup,
  originalMetadata?: IMerchandiseMetadata,
  changeRequestMetadata?: IMerchandiseMetadata
) => {
  merchandiseMetadataFieldKeys.forEach((field) => {
    addMerchandiseMetadataChange(
      productMetadataChanges,
      field,
      originalMetadata?.[field],
      changeRequestMetadata?.[field]
    );
  });
};

// Adds the user-facing display name because it can be either an override or the generated default.
const addDisplayNameChange = (
  productMetadataChanges: ICurrentProductMetadataChangesGroup,
  originalAlbum: IAlbum,
  changeRequestAlbum: IAlbum
) => {
  const originalDisplayName =
    originalAlbum.merchandiseMetadata?.displayNameOverride ??
    getMerchDefaultDisplayName(originalAlbum);
  const changeRequestDisplayName =
    changeRequestAlbum.merchandiseMetadata?.displayNameOverride ??
    getMerchDefaultDisplayName(changeRequestAlbum);

  addMerchandiseMetadataChange(
    productMetadataChanges,
    'displayName',
    originalDisplayName,
    changeRequestDisplayName
  );
};

// Merchandise products do not have tracks, so that group is always empty.
const removeUnsupportedMerchandiseGroups = (
  currentChanges: ICurrentChanges
): ICurrentChanges => ({
  ...currentChanges,
  tracksChanges: {
    changesCounter: 0,
    tracks: [],
  },
});

// Merch pricing changes come from albumSalesTeams, not albumMetaData.pricing.
const removeMerchandiseMetadataPricingChange = (
  currentChanges: ICurrentChanges
) => {
  const productMetadataPricingChange =
    currentChanges.productMetadataChanges.pricing;

  if (productMetadataPricingChange?.hasChanges) {
    currentChanges.productMetadataChanges.changesCounter = Math.max(
      currentChanges.productMetadataChanges.changesCounter - 1,
      0
    );
  }

  currentChanges.productMetadataChanges.pricing = {
    hasChanges: false,
    value: null,
  };
};

// Sums category counters after merchandise-specific fields and empty groups are applied.
const updateTotalChangesCounter = (
  currentChanges: ICurrentChanges
): ICurrentChanges => ({
  ...currentChanges,
  totalChangesCounter: [
    currentChanges.productMetadataChanges,
    currentChanges.tracksChanges,
    currentChanges.territoriesChanges,
    currentChanges.pricingChanges,
  ].reduce(
    (totalChangesCounter, changeGroup: CurrentChangesContainer) =>
      totalChangesCounter + changeGroup.changesCounter,
    0
  ),
});

// Builds current changes for merchandise while reusing the physical product comparison rules.
export const merchCurrentChanges = (
  originalAlbum: IAlbum,
  changeRequestAlbum: IAlbum
): ICurrentChanges => {
  const currentChanges = removeUnsupportedMerchandiseGroups(
    productCurrentChanges(
      {
        ...originalAlbum,
        tracks: [],
      },
      {
        ...changeRequestAlbum,
        tracks: [],
      }
    )
  );

  addMerchandiseMetadataChanges(
    currentChanges.productMetadataChanges,
    originalAlbum.merchandiseMetadata,
    changeRequestAlbum.merchandiseMetadata
  );
  addDisplayNameChange(
    currentChanges.productMetadataChanges,
    originalAlbum,
    changeRequestAlbum
  );
  removeMerchandiseMetadataPricingChange(currentChanges);

  return updateTotalChangesCounter(currentChanges);
};
